/**
 * A panel that slides up over the board - for Noticed, Observed and Add.
 *
 * Over the BOARD AREA only, not the page: the list on the left and the rail on
 * the right stay where they are, so it reads as part of the board rather than a
 * new screen. A light scrim over the board closes it; so do × and Escape (after
 * any drawer opened on top of it).
 *
 * ITS HEIGHT IS YOURS: drag the bar along its top edge, and it opens at that
 * height next time, in this browser. It never grows past the board area.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useEscapeTop } from '../../common/Drawer';
import { ResizeHandle, useResizablePane } from '../../useResizablePane';

const HEIGHT_KEY = 'jcseo.board.panelHeight';

export default function SlideUpPanel({
  title,
  onClose,
  children,
  testId,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  testId?: string;
}) {
  useEscapeTop(onClose);
  // HOW TALL IT CAN GET: the board area it sits in. Measured, so a drag stops at
  // the top rather than storing a height the board cannot show - which made
  // the next drag down seem stuck until it came back under the board's height.
  const areaRef = useRef<HTMLDivElement | null>(null);
  const [cap, setCap] = useState<number | null>(null);
  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    // Zero means not laid out yet (or a test with no layout): no cap from it.
    const measure = () => setCap(el.clientHeight > 0 ? Math.max(160, el.clientHeight - 8) : null);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const { width: height, dragging, onPointerDown, setWidth } = useResizablePane({
    storageKey: HEIGHT_KEY,
    initial: 420,
    min: 160,
    max: cap ?? 2000,
    handleOn: 'top',
  });
  // A size remembered from a taller window comes down to fit this one.
  useEffect(() => {
    if (cap && height > cap) setWidth(cap);
  }, [cap, height, setWidth]);
  return (
    <div ref={areaRef} className="absolute inset-0 z-20 flex flex-col justify-end" data-testid={testId}>
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-black/10 dark:bg-black/30"
      />
      <section
        role="dialog"
        aria-label={typeof title === 'string' ? title : undefined}
        // Also capped in CSS, for the moment before the first measurement.
        style={{ height, maxHeight: 'calc(100% - 0.5rem)' }}
        className="relative flex flex-col overflow-hidden rounded-t-xl border border-b-0 border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800 jc-slide-up"
        data-testid="slide-up-panel"
      >
        <ResizeHandle
          axis="y"
          grabber
          dragging={dragging}
          onPointerDown={onPointerDown}
          // Double-click puts it back to its usual height.
          onDoubleClick={() => setWidth(420)}
          title="Drag to resize. Double-click to reset."
          className="rounded-t-xl border-t-0"
        />
        <header className="flex items-center gap-2 border-b border-gray-100 px-4 py-2 dark:border-gray-700/60">
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close panel"
            className="ml-auto rounded px-2 py-0.5 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            ×
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto p-3">{children}</div>
      </section>
    </div>
  );
}
